import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Modal from "@/components/atoms/Modal";
import { Select, SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { pluralize } from "@/helpers/utils";
import useLoadOptions from "@/hooks/useLoadOptions";
import { DispatchCompaniesForm } from "@/interfaces/Company";
import { UserFilters } from "@/interfaces/IUser";
import { IModalMethods } from "@/interfaces/Modal";
import { GetActivitySectorsParams } from "@/services/activitySectors";
import { useDispatchCompaniesMutation } from "@/services/companies";
import { faTrash, faUser } from "@fortawesome/free-solid-svg-icons";
import {
  ElementRef,
  ForwardRefRenderFunction,
  Fragment,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { toast } from "react-toastify";

const PHONE_PREFIX_OPTIONS = [
  { value: "01", label: "01" },
  { value: "02", label: "02" },
  { value: "03", label: "03" },
  { value: "04", label: "04" },
  { value: "05", label: "05" },
  { value: "06", label: "06" },
  { value: "07", label: "07" },
  { value: "08", label: "08" },
  { value: "09", label: "09" },
];

const DispatchCompaniesModal: ForwardRefRenderFunction<IModalMethods> = (
  _,
  ref,
) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<DispatchCompaniesForm>({
    defaultValues: {
      data: [{ activity: null, user: null, count: 0, phonePrefixes: [] }],
    },
  });

  const [dispatchCompaniesMutation] = useDispatchCompaniesMutation();

  const { loadUsersOptions, loadActivitySectors } = useLoadOptions();

  const defaultAdditional = useRef<UserFilters>({
    pageIndex: 0,
    roles: ["SDR"],
    isActive: true,
  }).current;

  const defaultActivityAdditional = useRef<GetActivitySectorsParams>({
    pageIndex: 0,
    withMetadata: true,
  }).current;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<DispatchCompaniesForm> = async ({ data }) => {
    toast.promise(
      dispatchCompaniesMutation({
        data: data.map(({ user, activity, ...d }) => ({
          ...d,
          activitySectorId: activity?.id || null,
          userId: String(user?.id),
          phonePrefixes:
            d.phonePrefixes && d.phonePrefixes.length > 0
              ? d.phonePrefixes.map((e) => e.value)
              : undefined,
        })),
      }).unwrap(),
      {
        pending: "Dispatch en cours...",
        success: {
          render({ data }) {
            reset();
            return (
              <div>
                <div className="fw-bold mb-2">
                  ✅ {data?.dispatched}{" "}
                  {pluralize("entreprise", data?.dispatched)}{" "}
                  {pluralize("dispatchée", data?.dispatched)}!
                </div>
                {data?.assignments && data.assignments.length > 0 && (
                  <div className="mt-2">
                    <div className="small text-muted mb-1">Répartition :</div>
                    {data?.assignments.map((assignment: any, index: number) => (
                      <div
                        key={assignment.userId}
                        className="d-flex align-items-center gap-2 small"
                        style={{
                          padding: "4px 8px",
                          backgroundColor:
                            index % 2 === 0
                              ? "rgba(0,0,0,0.03)"
                              : "transparent",
                          borderRadius: "4px",
                        }}
                      >
                        <span className="fw-semibold">
                          {assignment.fullname}
                        </span>
                        <span className="ms-auto badge bg-primary">
                          {assignment.count}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          },
        },
        error: "Une erreur est survenue lors du dispatch.",
      },
      { autoClose: 10000 },
    );
  };

  const onError: SubmitErrorHandler<DispatchCompaniesForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Dispatch des entreprises"
      requiredLabel
      onSubmit={handleSubmit(onSubmit, onError)}
      beforeFooter={
        <Flex className="me-auto">
          <IconButton
            variant="primary"
            size="sm"
            icon={faUser}
            transform="shrink-3"
            iconAlign="middle"
            className="me-2"
            onClick={() =>
              append({
                activity: null,
                user: null,
                count: 0,
                phonePrefixes: [],
              })
            }
          >
            <span className="d-none d-lg-inline-block ms-1">Telepro</span>
          </IconButton>
        </Flex>
      }
    >
      {fields.map(({ id }, index) => (
        <Fragment key={id}>
          {index > 0 && <hr className="my-4" />}
          <div className="position-relative">
            {index > 0 && (
              <div
                className="position-absolute top-0 end-0"
                style={{ zIndex: 10 }}
              >
                <IconButton
                  title="Supprimer"
                  icon={faTrash}
                  size="sm"
                  variant="danger"
                  onClick={() => remove(index)}
                />
              </div>
            )}
            <Row className="gx-3 gy-3">
              <Col xs={12} lg={5}>
                <Form.Group>
                  <Form.Label>
                    Télépros : <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    control={control}
                    name={`data.${index}.user`}
                    rules={{
                      required: "Le télépro est obligatoire",
                    }}
                    render={({ field: { value, onChange } }) => (
                      <SelectPaginateAsync
                        loadOptions={loadUsersOptions}
                        getOptionLabel={(option) => option.fullname}
                        getOptionValue={(option) => option.id.toString()}
                        isClearable
                        value={value}
                        onChange={onChange}
                        aria-invalid={!!errors.data?.[index]?.user}
                        additional={defaultAdditional}
                      />
                    )}
                  />
                  {errors.data?.[index]?.user && (
                    <div className="invalid-feedback d-block">
                      {errors.data?.[index]?.user?.message}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Group>
                  <Form.Label>
                    Nombre : <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="ex : 15"
                    type="number"
                    {...register(`data.${index}.count`, {
                      required: "Ce champ est requis",
                      min: {
                        value: 1,
                        message: "Ce champ doit etre superieur a 0",
                      },
                      max: {
                        value: 100,
                        message: "Maximum de 100",
                      },
                    })}
                    isInvalid={!!errors.data?.[index]?.count}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.data?.[index]?.count?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Form.Group>
                  <Form.Label>
                    Secteur d'activité : <span className="text-danger">*</span>
                  </Form.Label>
                  <Controller
                    control={control}
                    name={`data.${index}.activity`}
                    rules={{
                      required: "Le télépro est obligatoire",
                    }}
                    render={({ field: { value, onChange } }) => (
                      <SelectPaginateAsync
                        loadOptions={loadActivitySectors}
                        getOptionLabel={(option) => {
                          const companiesCount =
                            option.$extras?.companiesCount ?? 0;
                          const withoutSdr =
                            option.$extras?.companiesWithoutSdrCount ?? 0;
                          return `${option.opco.name} - ${
                            option.name
                          } (${companiesCount} ${pluralize(
                            "entreprise",
                            companiesCount,
                          )}, ${withoutSdr} sans SDR)`;
                        }}
                        getOptionValue={(option) => option.id.toString()}
                        isClearable
                        value={value}
                        onChange={onChange}
                        additional={defaultActivityAdditional}
                        aria-invalid={!!errors.data?.[index]?.user}
                      />
                    )}
                  />

                  <Form.Control.Feedback type="invalid">
                    {errors.data?.[index]?.activity?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Prefixe téléphone</Form.Label>
                  <Controller
                    control={control}
                    name={`data.${index}.phonePrefixes`}
                    render={({ field: { onChange } }) => (
                      <Select
                        isMulti
                        options={PHONE_PREFIX_OPTIONS}
                        onChange={onChange}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Fragment>
      ))}
    </Modal>
  );
};

export default forwardRef(DispatchCompaniesModal);
